import { Badge, Box, Button, Flex, Group, Table, Text } from "@mantine/core";
import {
  IconCalendar,
  IconClock,
  IconFlask,
  IconPackage,
  IconStack3,
} from "@tabler/icons-react";
import { AppProduct, MukundaProductData } from "api/product-search";
import React, { useState } from "react";

import {
  FindByImageModal,
  FindSaltModal,
} from "../FindSaltModal/FindSaltModal";

interface ProductResultsProps {
  products: (AppProduct | MukundaProductData)[];
  searchTerm: string;
  onProductClick: (productId: string) => void;
  onAddToCart: (productId: string) => void;
}

export const ProductResults = ({
  products,
  onProductClick,
}: ProductResultsProps) => {
  const [appliedSalts, setAppliedSalts] = useState<Record<string, string>>({});
  const [selectedProduct, setSelectedProduct] = useState<
    AppProduct | MukundaProductData | null
  >(null);
  const [findSaltOpened, setFindSaltOpened] = useState(false);
  const [findByImageOpened, setFindByImageOpened] = useState(false);

  if (!products || products.length === 0) {
    return null;
  }

  return (
    <Box>
      <Group spacing="xs" mb="md" align="center" position="apart">
        <Group spacing="xs" align="center">
          <IconPackage size={20} color="#DC2626" />
          <Text fw={500} size="lg" color="#DC2626" style={{ fontSize: "14px" }}>
            Available Products
          </Text>
        </Group>
        <Badge
          size="sm"
          color="red"
          variant="light"
          style={{
            backgroundColor: "#FEE2E2",
            color: "#DC2626",
            fontSize: "12px",
            fontWeight: 600,
          }}
        >
          {products.length} found
        </Badge>
      </Group>

      {products.map((product, idx) => {
        // Handle both AppProduct and MukundaProductData types
        const formattedExpiryDate = product.expiryDate
          ? product.expiryDate
              .replace(/_/g, " ")
              .replace(
                /^(LONG EXPIRY|SHORT EXPIRY|MEDIUM EXPIRY|EXPIRED)\s*/i,
                ""
              )
          : "";

        // Get expiry type - handle both data structures
        const expiryType =
          "expiryType" in product ? product.expiryType : undefined;
        const stockType =
          "stockType" in product ? product.stockType : undefined;
        const isPrRateToBeShown =
          "isPrRateToBeShown" in product ? product.isPrRateToBeShown : true;

        return (
          <Box
            key={idx}
            style={{
              background: "#fff",
              borderRadius: 12,
              boxShadow: "0 1px 4px #f0f1f2",
              padding: 16,
              marginBottom: 12,
              cursor: "pointer",
              minWidth: 0,
            }}
            onClick={() => onProductClick(product.id)}
          >
            <Flex
              justify="space-between"
              align="flex-start"
              style={{ width: "100%" }}
            >
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Text
                  fw={800}
                  color="red.8"
                  style={{
                    fontSize: "0.895em",
                    lineHeight: "1.24rem",
                    marginBottom: 5,
                  }}
                >
                  {product.productName}
                </Text>
                <Text
                  size="sm"
                  style={{
                    color: "rgb(107 114 128)",
                    fontSize: "0.775em",
                    lineHeight: "1.24rem",
                    marginTop: 0,
                    marginLeft: 1,
                  }}
                >
                  By {product.manufacturerName}
                </Text>
                {appliedSalts[product.id] ||
                (product.saltInfo && product.saltInfo.trim() !== "") ? (
                  <Text
                    size="sm"
                    c="#0A0A0A"
                    style={{
                      marginTop: 2,
                      fontSize: "0.775em",
                      lineHeight: "1.24rem",
                      fontWeight: 500,
                    }}
                  >
                    {appliedSalts[product.id] || product.saltInfo}
                  </Text>
                ) : (
                  <Flex gap="xs" mt={6} align="center">
                    <Button
                      size="xs"
                      variant="outline"
                      color="blue"
                      leftIcon={<IconFlask size={14} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedProduct(product);
                        setFindSaltOpened(true);
                      }}
                      styles={{
                        root: {
                          borderColor: "#2B87EB",
                          color: "#2B87EB",
                          height: 28,
                          fontSize: "11px",
                          fontWeight: 500,
                          backgroundColor: "transparent",
                          "&:hover": {
                            backgroundColor: "#f0f7ff",
                          },
                        },
                      }}
                    >
                      Find Salt
                    </Button>
                    {/* <Button
                      size="xs"
                      variant="outline"
                      color="gray"
                      leftIcon={<IconPhoto size={14} />}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      styles={{
                        root: {
                          borderColor: "#9CA3AF",
                          color: "#4B5563",
                          height: 28,
                          fontSize: "11px",
                          fontWeight: 500,
                          backgroundColor: "transparent",
                          "&:hover": {
                            backgroundColor: "#F3F4F6",
                          },
                        },
                      }}
                    >
                      Find by Image
                    </Button> */}
                  </Flex>
                )}
              </Box>
              <Flex
                direction="column"
                align="flex-end"
                gap={6}
                style={{ marginTop: 2, minWidth: 0 }}
              >
                {product.expiryDate && (
                  <Flex
                    align="center"
                    gap={4}
                    style={{
                      fontWeight: 600,
                      fontSize: "0.95em",
                    }}
                  >
                    <IconCalendar
                      size={15}
                      color={
                        expiryType?.value === "LONG_EXPIRY"
                          ? "rgb(22 163 74)"
                          : expiryType?.value === "MEDIUM_EXPIRY"
                          ? "#F59E42"
                          : "#DC2626"
                      }
                      style={{
                        display: "block",
                        marginBottom: 2,
                      }}
                    />
                    <span
                      style={{
                        color:
                          expiryType?.value === "LONG_EXPIRY"
                            ? "rgb(22 163 74)"
                            : expiryType?.value === "MEDIUM_EXPIRY"
                            ? "#F59E42"
                            : "#DC2626",
                        fontWeight: 500,
                        fontSize: "0.85em",
                        lineHeight: "1rem",
                      }}
                    >
                      {formattedExpiryDate}
                    </span>
                  </Flex>
                )}
                {product.stock && (
                  <Flex
                    align="center"
                    gap={4}
                    style={{
                      color:
                        stockType?.value === "LOW_STOCK"
                          ? "#F97316"
                          : "#64748B",
                      fontWeight: 500,
                      fontSize: "0.85em",
                      lineHeight: "1rem",
                    }}
                  >
                    <IconStack3
                      size={15}
                      color={
                        stockType?.value === "LOW_STOCK" ? "#F97316" : "#64748B"
                      }
                      style={{ display: "block" }}
                    />
                    <span
                      style={{
                        color:
                          stockType?.value === "LOW_STOCK"
                            ? "#F97316"
                            : "#64748B",
                        fontWeight: 500,
                        fontSize: "0.85em",
                        lineHeight: "1rem",
                      }}
                    >
                      {product.stock.value}
                    </span>
                  </Flex>
                )}
                {product.age && (
                  <Flex
                    align="center"
                    gap={4}
                    style={{
                      color: "#64748B",
                      fontWeight: 600,
                      fontSize: "0.95em",
                    }}
                  >
                    <IconClock
                      size={15}
                      color="#64748B"
                      style={{
                        display: "block",
                        marginBottom: 1,
                      }}
                    />
                    <span
                      style={{
                        color: "#64748B",
                        fontWeight: 500,
                        fontSize: "0.85em",
                        lineHeight: "1rem",
                      }}
                    >
                      {product.age}d
                    </span>
                  </Flex>
                )}
              </Flex>
            </Flex>
            {/* Pricing and margin info as a table */}
            <Box
              style={{
                marginTop: 6,
                marginLeft: 0,
                width: "auto",
              }}
            >
              <Table withBorder={false} withColumnBorders={false}>
                <thead>
                  <tr>
                    {isPrRateToBeShown && (
                      <th
                        style={{
                          textAlign: "left",
                          color: "#737373",
                          fontWeight: 500,
                          fontSize: "0.75em",
                          lineHeight: "1rem",
                        }}
                      >
                        PRate
                      </th>
                    )}
                    <th
                      style={{
                        textAlign: "left",
                        color: "#737373",
                        fontWeight: 500,
                        fontSize: "0.75em",
                        lineHeight: "1rem",
                      }}
                    >
                      SRate
                    </th>
                    <th
                      style={{
                        textAlign: "left",
                        color: "#737373",
                        fontWeight: 500,
                        fontSize: "0.75em",
                        lineHeight: "1rem",
                      }}
                    >
                      PTR
                    </th>
                    <th
                      style={{
                        textAlign: "left",
                        color: "#737373",
                        fontWeight: 500,
                        fontSize: "0.75em",
                        lineHeight: "1rem",
                      }}
                    >
                      MRP
                    </th>
                    <th
                      style={{
                        textAlign: "left",
                        color: "#737373",
                        fontWeight: 500,
                        fontSize: "0.75em",
                        lineHeight: "1rem",
                      }}
                    >
                      B2B Margin
                    </th>
                    <th
                      style={{
                        textAlign: "left",
                        color: "#737373",
                        fontWeight: 500,
                        fontSize: "0.75em",
                        lineHeight: "1rem",
                      }}
                    >
                      B2C Margin
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    {isPrRateToBeShown && (
                      <td
                        style={{
                          fontWeight: 500,
                          fontSize: "0.75em",
                          lineHeight: "1rem",
                          color: "#16A34A",
                        }}
                      >
                        {"prate" in product
                          ? product.prate?.formattedValue ?? "-"
                          : "-"}
                      </td>
                    )}
                    <td
                      style={{
                        fontWeight: 500,
                        fontSize: "0.75em",
                        lineHeight: "1rem",
                        color: "#16A34A",
                      }}
                    >
                      {"srate" in product
                        ? product.srate?.formattedValue ?? "-"
                        : "-"}
                    </td>
                    <td
                      style={{
                        fontWeight: 500,
                        fontSize: "0.75em",
                        lineHeight: "1rem",
                        color: "#0A0A0A",
                      }}
                    >
                      {product.ptr?.formattedValue ?? "-"}
                    </td>
                    <td
                      style={{
                        fontWeight: 500,
                        fontSize: "0.75em",
                        lineHeight: "1rem",
                        color: "#DC2626",
                      }}
                    >
                      {product.mrp?.formattedValue ?? "-"}
                    </td>
                    <td
                      style={{
                        fontWeight: 500,
                        fontSize: "0.75em",
                        lineHeight: "1rem",
                        color: "#16A34A",
                      }}
                    >
                      {product.marginPercent?.formattedValue ?? "-"}
                    </td>
                    <td
                      style={{
                        fontWeight: 500,
                        fontSize: "0.75em",
                        lineHeight: "1rem",
                        color: "#16A34A",
                      }}
                    >
                      {product.b2cMargin?.formattedValue ?? "-"}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Box>
          </Box>
        );
      })}

      {selectedProduct && (
        <FindSaltModal
          opened={findSaltOpened}
          onClose={() => {
            setFindSaltOpened(false);
            setSelectedProduct(null);
          }}
          productUuid={selectedProduct.id}
          productName={selectedProduct.productName}
          manufacturerName={selectedProduct.manufacturerName}
          packageForm={
            "packageForm" in selectedProduct
              ? selectedProduct.packageForm
              : undefined
          }
          onApply={(saltName) => {
            setAppliedSalts((prev) => ({
              ...prev,
              [selectedProduct.id]: saltName,
            }));
          }}
        />
      )}

      {selectedProduct && (
        <FindByImageModal
          opened={findByImageOpened}
          onClose={() => {
            setFindByImageOpened(false);
            setSelectedProduct(null);
          }}
          productUuid={selectedProduct.id}
          productName={selectedProduct.productName}
          onApply={(saltName) => {
            setAppliedSalts((prev) => ({
              ...prev,
              [selectedProduct.id]: saltName,
            }));
          }}
        />
      )}
    </Box>
  );
};
